import React from "react";
import { X, Award, Clock, Grid, CheckCircle, Settings } from "lucide-react";

const HowToPlayModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <div className="flex justify-between items-center mb-5">
                  <h3
                    className="text-2xl leading-6 font-bold text-purple-700"
                    id="modal-title"
                  >
                    How to Play Bingo
                  </h3>
                  <button
                    type="button"
                    className="bg-white rounded-full p-1 hover:bg-gray-100 focus:outline-none"
                    onClick={onClose}
                  >
                    <X size={24} className="text-gray-500" />
                  </button>
                </div>

                <div className="mt-4 space-y-8">
                  {/* General Instructions */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Award className="text-purple-600 mr-2" size={20} />
                      The Basics
                    </h4>
                    <p className="mt-2 text-gray-600">
                      Bingo is a game of chance where players mark off numbers
                      on a card as they're called out randomly. The goal is to
                      be the first to complete a specific pattern.
                    </p>
                  </div>

                  {/* Step 1 */}
                  <div className="pl-5 border-l-2 border-purple-200">
                    <h4 className="text-md font-semibold text-gray-900">
                      1. Choose Your Game
                    </h4>
                    <p className="mt-1 text-gray-600">
                      Select your bingo variant (15, 30, 50, 75, or 90-ball)
                      from the dropdown menu. Each variant has a different
                      number of balls and slightly different rules.
                    </p>
                    <div className="mt-2 flex items-center text-sm text-purple-600">
                      <Settings size={16} className="mr-1" />
                      <span>
                        Tip: For beginners, 75-ball bingo is most common in the
                        US.
                      </span>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="pl-5 border-l-2 border-purple-200">
                    <h4 className="text-md font-semibold text-gray-900">
                      2. Select a Winning Pattern
                    </h4>
                    <p className="mt-1 text-gray-600">
                      Choose the pattern players need to complete to win.
                      Options range from classic horizontal/vertical lines to
                      fun shapes like letters and special patterns.
                    </p>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div className="bg-purple-50 p-2 rounded text-center">
                        <span className="text-lg">🎯</span>
                        <p className="text-xs text-gray-600">Classic</p>
                      </div>
                      <div className="bg-purple-50 p-2 rounded text-center">
                        <span className="text-lg">❌</span>
                        <p className="text-xs text-gray-600">Letter X</p>
                      </div>
                      <div className="bg-purple-50 p-2 rounded text-center">
                        <span className="text-lg">⬛</span>
                        <p className="text-xs text-gray-600">Blackout</p>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="pl-5 border-l-2 border-purple-200">
                    <h4 className="text-md font-semibold text-gray-900">
                      3. Set Up Your Game
                    </h4>
                    <p className="mt-1 text-gray-600">
                      Adjust the game speed using the slider. A slower speed
                      gives players more time to find and mark numbers. You can
                      also set the total game duration.
                    </p>
                    <div className="mt-2 flex items-center text-sm text-purple-600">
                      <Clock size={16} className="mr-1" />
                      <span>
                        Tip: For groups of 10+ people, use a slower speed.
                      </span>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="pl-5 border-l-2 border-purple-200">
                    <h4 className="text-md font-semibold text-gray-900">
                      4. Start Calling Numbers
                    </h4>
                    <p className="mt-1 text-gray-600">
                      Click "Start / Stop!" to begin calling numbers randomly.
                      Each player should mark these numbers on their card. You
                      can pause at any time with the "Stop" button or by
                      pressing any key.
                    </p>
                  </div>

                  {/* Step 5 */}
                  <div className="pl-5 border-l-2 border-purple-200">
                    <h4 className="text-md font-semibold text-gray-900">
                      5. Winning the Game
                    </h4>
                    <p className="mt-1 text-gray-600">
                      A player wins when they complete the selected pattern and
                      call out "Bingo!" You can verify their card by checking
                      the called numbers list.
                    </p>
                    <div className="mt-2 flex items-center text-sm text-purple-600">
                      <CheckCircle size={16} className="mr-1" />
                      <span>
                        Make sure to have a fun prize ready for the winner!
                      </span>
                    </div>
                  </div>

                  {/* Printable Cards Section */}
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="text-md font-semibold text-gray-900 flex items-center">
                      <Grid className="text-purple-600 mr-2" size={20} />
                      Need Bingo Cards?
                    </h4>
                    <p className="mt-1 text-gray-600">
                      Use our "Printable Cards" feature to generate and print
                      bingo cards for all your players. You can customize them
                      based on your selected game type.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Got it!
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => window.open("/printable-cards", "_blank")}
            >
              Print Cards
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToPlayModal;
